// Hooks da marca d'agua. Leitura via RLS, escrita via RPC SECURITY DEFINER.
// A previa e gerada pelo bot (mesmo renderizador do envio): o painel pede por
// RPC, acompanha a linha da fila e exibe o JPEG do Storage por URL assinada.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  normalizeWatermarkSettings,
  type WatermarkDesign,
  type WatermarkFlow,
  type WatermarkSettings,
} from "@lucralink/shared";
import { supabase } from "@/lib/supabase";
import { checkLogoFile, logoPathFor, watermarkErrorMessage } from "@/lib/watermark";

export const WATERMARK_BUCKET = "watermark-assets";
const SETTINGS_KEY = ["watermark-settings"] as const;
export const PREVIEW_DEBOUNCE_MS = 500;
export const PREVIEW_POLL_MS = 400;
export const PREVIEW_TIMEOUT_MS = 15_000;

async function fetchSettings(): Promise<WatermarkSettings> {
  const { data, error } = await supabase
    .from("watermark_settings")
    .select("*")
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return normalizeWatermarkSettings(data);
}

export function useWatermarkSettings(enabled = true) {
  return useQuery<WatermarkSettings, Error>({
    queryKey: SETTINGS_KEY,
    queryFn: fetchSettings,
    enabled,
  });
}

export interface SaveWatermarkInput {
  readonly enabled: boolean;
  readonly applyTo: "all" | "selected";
  readonly flows: readonly WatermarkFlow[];
  readonly design: WatermarkDesign;
}

export function useSaveWatermark() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SaveWatermarkInput>({
    mutationFn: async (input) => {
      const { error } = await supabase.rpc("save_watermark_settings", {
        p_enabled: input.enabled,
        p_apply_to: input.applyTo,
        p_flows: [...input.flows],
        p_design: input.design,
      });
      if (error) throw new Error(watermarkErrorMessage(error));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

async function removeObject(path: string): Promise<void> {
  const { error } = await supabase.storage.from(WATERMARK_BUCKET).remove([path]);
  if (error) throw new Error("O logo antigo não pôde ser apagado do armazenamento.");
}

/** Apaga sem derrubar o fluxo: devolve o aviso (ou null) para a tela mostrar. */
async function removeObjectWarning(path: string): Promise<string | null> {
  try {
    await removeObject(path);
    return null;
  } catch (cleanup) {
    return cleanup instanceof Error ? cleanup.message : "Falha ao apagar o logo antigo.";
  }
}

/** Sobe o logo novo, aponta a config para ele e apaga o anterior. */
export function useUploadWatermarkLogo(tenantId: string | null, currentPath: string | null) {
  const queryClient = useQueryClient();
  return useMutation<string | null, Error, File>({
    mutationFn: async (file) => {
      const check = checkLogoFile(file);
      if (!check.ok) throw new Error(check.message);
      if (!tenantId) throw new Error("Empresa não carregada.");
      const path = logoPathFor(tenantId, crypto.randomUUID(), check.extension);
      const upload = await supabase.storage
        .from(WATERMARK_BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upload.error) throw new Error("Não foi possível enviar o logo.");
      const { error } = await supabase.rpc("set_watermark_logo", { p_path: path });
      if (error) {
        // A config nao aponta para o arquivo novo: nao deixa lixo no bucket.
        await removeObjectWarning(path);
        throw new Error(watermarkErrorMessage(error));
      }
      return currentPath ? removeObjectWarning(currentPath) : null;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

/** Remove o logo da config; falha ao apagar o arquivo vira aviso (o logo ja saiu da marca). */
export function useRemoveWatermarkLogo(currentPath: string | null) {
  const queryClient = useQueryClient();
  return useMutation<string | null, Error, void>({
    mutationFn: async () => {
      const { error } = await supabase.rpc("set_watermark_logo", { p_path: null });
      if (error) throw new Error(watermarkErrorMessage(error));
      return currentPath ? removeObjectWarning(currentPath) : null;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

export async function signedWatermarkUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(WATERMARK_BUCKET)
    .createSignedUrl(path, 60 * 10);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export function useLogoUrl(path: string | null) {
  return useQuery<string | null, Error>({
    queryKey: ["watermark-logo-url", path],
    queryFn: () => (path ? signedWatermarkUrl(path) : Promise.resolve(null)),
    staleTime: 5 * 60_000,
  });
}

export type PreviewStatus = "idle" | "rendering" | "ready" | "error";

export interface PreviewState {
  readonly status: PreviewStatus;
  readonly url: string | null;
  readonly error: string | null;
}

interface PreviewRow {
  readonly status: string;
  readonly storage_path: string | null;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Acompanha a linha da fila ate done/failed ou o tempo esgotar. */
async function waitForPreview(id: string, isCurrent: () => boolean): Promise<string> {
  const deadline = Date.now() + PREVIEW_TIMEOUT_MS;
  while (Date.now() < deadline && isCurrent()) {
    const { data, error } = await supabase
      .from("watermark_preview_requests")
      .select("status, storage_path")
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error("Não foi possível acompanhar a prévia.");
    const row = data as PreviewRow | null;
    if (row?.status === "done" && row.storage_path) {
      const url = await signedWatermarkUrl(row.storage_path);
      if (!url) throw new Error("Não foi possível abrir a prévia.");
      return url;
    }
    if (row?.status === "failed")
      throw new Error("A prévia falhou. Ajuste o desenho e tente de novo.");
    await sleep(PREVIEW_POLL_MS);
  }
  throw new Error("O serviço de prévia não respondeu. Tente de novo em instantes.");
}

/** Previa ao vivo: pede ao bot (debounce) a cada mudanca do design e do logo. */
export function useWatermarkPreview(
  design: WatermarkDesign,
  logoPath: string | null,
  active: boolean,
): PreviewState {
  const [state, setState] = useState<PreviewState>({ status: "idle", url: null, error: null });
  const generation = useRef(0);
  const key = JSON.stringify(design);

  useEffect(() => {
    if (!active) return;
    const current = ++generation.current;
    const isCurrent = () => generation.current === current;
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, status: "rendering", error: null }));
      void (async () => {
        try {
          const { data, error } = await supabase.rpc("request_watermark_preview", {
            p_design: JSON.parse(key) as unknown,
          });
          if (error) throw new Error(watermarkErrorMessage(error));
          const url = await waitForPreview(String(data), isCurrent);
          if (isCurrent()) setState({ status: "ready", url, error: null });
        } catch (failure) {
          if (isCurrent()) {
            setState((prev) => ({
              ...prev,
              status: "error",
              error: failure instanceof Error ? failure.message : String(failure),
            }));
          }
        }
      })();
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key, logoPath, active]);

  return state;
}
